/* eslint-disable react-refresh/only-export-components */
import type {
  BroadviewApiClient,
  BroadviewClientMode,
  createClaimEvidenceClient,
} from "@broadview/api-client";
import type { TenantContext } from "@broadview/api-client";
import { createContext, useContext, type ReactNode } from "react";
import type { PortalAuthClient } from "./portal-auth";

export type PortalProblemRecord = Readonly<{
  id: string;
  tenantId: string;
  displayId: string;
  title: string;
  summary: string;
  severity: "UNASSESSED" | "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  status: "DRAFT" | "OPEN" | "ON_HOLD" | "CLOSED";
  ownerUserId: string | null;
  version: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  classification?: "HISTORICAL_REAL" | "OPERATIONAL";
  claimCount?: number;
  impact?: Readonly<{ amountMinor: string; currencyCode: string }>;
  dataCutoffAt?: string;
  freshness?: "CURRENT" | "STALE" | "UNKNOWN";
  quality?: "VERIFIED" | "PARTIAL";
  reconciliation?: "PASSED" | "EXCEPTION" | "UNKNOWN";
  sourceSummary?: string;
}>;

export type PortalProblemRecordReader = Readonly<{
  list(tenantId: string): Promise<readonly PortalProblemRecord[]>;
  get(tenantId: string, id: string): Promise<PortalProblemRecord>;
}>;

export type PortalRuntime = Readonly<{
  client: BroadviewApiClient;
  mode: BroadviewClientMode;
  tenantContext: TenantContext;
  problemRecords?: PortalProblemRecordReader;
  claims?: ReturnType<typeof createClaimEvidenceClient>;
  auth?: Pick<
    PortalAuthClient,
    "getProfile" | "requestPasswordRecovery" | "updateProfile" | "updatePassword"
  >;
  signOut(): Promise<void>;
}>;

const PortalRuntimeContext = createContext<PortalRuntime | null>(null);

export function PortalRuntimeProvider({
  runtime,
  children,
}: Readonly<{ runtime: PortalRuntime; children: ReactNode }>) {
  return <PortalRuntimeContext.Provider value={runtime}>{children}</PortalRuntimeContext.Provider>;
}

export function usePortalRuntime(): PortalRuntime {
  const runtime = useContext(PortalRuntimeContext);
  if (!runtime)
    throw new Error("Portal runtime is unavailable outside an authenticated workspace.");
  return runtime;
}
